import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client.safe';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook that automatically tracks login sessions for testers (is_tester = true).
 * Creates a session on login, updates it periodically, and ends it on logout.
 */
export const useTesterSessionTracking = () => {
  const sessionIdRef = useRef<string | null>(null);
  const heartbeatRef = useRef<number | null>(null);
  const isTesterRef = useRef<boolean>(false);

  // End current session
  const endSession = useCallback(async () => {
    if (!sessionIdRef.current) return;
    
    try {
      console.log('[TesterSession] Ending session:', sessionIdRef.current);
      await supabase
        .from('tester_sessions')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', sessionIdRef.current);
      
      sessionIdRef.current = null;
    } catch (error) {
      console.error('[TesterSession] Failed to end session:', error);
    }
  }, []);

  // Start new session
  const startSession = useCallback(async (userId: string) => {
    try {
      // First, end any existing open sessions for this user
      await supabase
        .from('tester_sessions')
        .update({ ended_at: new Date().toISOString() })
        .eq('user_id', userId)
        .is('ended_at', null);

      // Create new session
      const { data, error } = await supabase
        .from('tester_sessions')
        .insert({
          user_id: userId,
          started_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) {
        console.error('[TesterSession] Failed to create session:', error);
        return;
      }

      sessionIdRef.current = data.id;
      console.log('[TesterSession] Started session:', data.id);
    } catch (error) {
      console.error('[TesterSession] Error starting session:', error);
    }
  }, []);

  // Heartbeat to keep session alive
  const heartbeat = useCallback(async () => {
    if (!sessionIdRef.current) return;
    
    try {
      // Update ended_at to now (will be recalculated on actual end)
      await supabase
        .from('tester_sessions')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', sessionIdRef.current);
      
      console.log('[TesterSession] Heartbeat sent');
    } catch (error) {
      console.error('[TesterSession] Heartbeat failed:', error);
    }
  }, []);

  // Check if current user is a tester
  const checkIfTester = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_tester')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('[TesterSession] Failed to check tester status:', error);
        return false;
      }

      return data?.is_tester === true;
    } catch (error) {
      console.error('[TesterSession] Error checking tester status:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    // Setup heartbeat interval
    const setupHeartbeat = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      heartbeatRef.current = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    };

    const clearHeartbeat = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };

    // Listen for auth state changes
    // IMPORTANT: Keep this callback synchronous to avoid Supabase auth deadlocks.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[TesterSession] Auth event:', event);

      if (event === 'SIGNED_IN' && session?.user) {
        // Defer all Supabase calls to avoid blocking auth state propagation.
        setTimeout(() => {
          void (async () => {
            const isTester = await checkIfTester(session.user.id);
            isTesterRef.current = isTester;

            if (isTester) {
              await startSession(session.user.id);
              setupHeartbeat();
            }
          })();
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        setTimeout(() => {
          void (async () => {
            if (isTesterRef.current) {
              await endSession();
              clearHeartbeat();
            }
            isTesterRef.current = false;
          })();
        }, 0);
      }
    });

    // Check initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const isTester = await checkIfTester(session.user.id);
        isTesterRef.current = isTester;

        if (isTester && !sessionIdRef.current) {
          await startSession(session.user.id);
          setupHeartbeat();
        }
      }
    });

    // Cleanup on unmount or page close
    const handleBeforeUnload = () => {
      if (isTesterRef.current && sessionIdRef.current) {
        // Use sendBeacon for reliable delivery on page close
        const payload = JSON.stringify({
          session_id: sessionIdRef.current,
          ended_at: new Date().toISOString()
        });
        
        // Best effort - sendBeacon doesn't support auth headers
        // The heartbeat will have updated ended_at recently
        console.log('[TesterSession] Page unloading, session will be marked as ended');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      subscription.unsubscribe();
      clearHeartbeat();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [checkIfTester, startSession, endSession, heartbeat]);

  return {
    isTracking: isTesterRef.current,
    currentSessionId: sessionIdRef.current
  };
};
